import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, Search, Trash2, Edit2, Users, Mail, Phone,
  Home, Briefcase, Copy, Link2, ExternalLink, Shield, Key, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

// Generate unique agent portal link
const getAgentPortalLink = (agentId) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/agent/portal/${agentId}`;
};

// Copy link to clipboard
const copyToClipboard = async (text, agentName) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`Portal link for ${agentName} copied to clipboard!`);
  } catch (err) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    toast.success(`Portal link for ${agentName} copied to clipboard!`);
  }
};

export default function Agents() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [stats, setStats] = useState({ total: 0, mortgage_count: 0, insurance_count: 0, dual_license_count: 0 });
  
  // State for SIN visibility toggle
  const [showSin, setShowSin] = useState({});
  
  // State for password management
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedAgentForPassword, setSelectedAgentForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordInfo, setPasswordInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    email: '',
    phone: '',
    status: 'active',
    sin: '',
    employment_type: 'contractor',
    // License selection
    has_mortgage_license: false,
    has_insurance_license: false,
    // Mortgage-specific
    split_percentage: 0.85,
    // Insurance-specific splits
    fyo_split: 100,
    fyc_split: 100,
    renewal_split: 100,
    transfer_split: 100
  });

  useEffect(() => {
    loadAgents();
  }, [divisionFilter]);

  const loadAgents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/agents/all/unified?division_filter=${divisionFilter}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setAgents(response.data.agents || []);
      setStats({
        total: response.data.total || 0,
        mortgage_count: response.data.mortgage_count || 0,
        insurance_count: response.data.insurance_count || 0,
        dual_license_count: response.data.dual_license_count || 0
      });
    } catch (err) {
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      email: '',
      phone: '',
      status: 'active',
      sin: '',
      employment_type: 'contractor',
      has_mortgage_license: false,
      has_insurance_license: false,
      split_percentage: 0.85,
      fyo_split: 100,
      fyc_split: 100,
      renewal_split: 100,
      transfer_split: 100
    });
    setEditingAgent(null);
  };

  const openEdit = (agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name || '',
      address: agent.address || '',
      email: agent.email || '',
      phone: agent.phone || '',
      status: agent.status || 'active',
      sin: agent.sin || '',
      employment_type: agent.employment_type || 'contractor',
      has_mortgage_license: agent.has_mortgage_license,
      has_insurance_license: agent.has_insurance_license,
      split_percentage: agent.mortgage_split || 0.85,
      fyo_split: agent.fyo_split || 100,
      fyc_split: agent.fyc_split || 100,
      renewal_split: agent.renewal_split || 100,
      transfer_split: agent.transfer_split || 100
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.has_mortgage_license && !formData.has_insurance_license) {
      toast.error('Please select at least one license');
      return;
    }
    
    try {
      if (editingAgent) {
        // Update existing agent
        await axios.put(
          `${API}/api/agents/all/unified/${editingAgent.id}`,
          formData,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        toast.success('Agent updated');
      } else {
        // Create new agent
        await axios.post(
          `${API}/api/agents/all/unified`,
          formData,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        toast.success('Agent created');
      }
      
      setShowModal(false);
      resetForm();
      loadAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (agent) => {
    if (!window.confirm(`Delete ${agent.name}? This will remove them from all divisions.`)) return;
    
    try {
      // Delete from both collections if exists
      if (agent.mortgage_id) {
        await axios.delete(`${API}/api/agents/${agent.mortgage_id}`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
      }
      if (agent.insurance_id) {
        await axios.delete(`${API}/api/insurance/agents/${agent.insurance_id}`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
      }
      toast.success('Agent deleted');
      loadAgents();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  // Password management functions
  const openPasswordModal = async (agent) => {
    setSelectedAgentForPassword(agent);
    setNewPassword('');
    setPasswordInfo(null);
    setShowPasswordModal(true);
    
    // Load password info
    try {
      // Find user by email
      const response = await axios.get(
        `${API}/api/users/by-email/${encodeURIComponent(agent.email)}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      if (response.data?.id) {
        const infoResponse = await axios.get(
          `${API}/api/auth/admin/user-password-info/${response.data.id}`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        setPasswordInfo(infoResponse.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "An error occurred");
    }
  };

  const setAgentPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      // Find user by email
      const userResponse = await axios.get(
        `${API}/api/users/by-email/${encodeURIComponent(selectedAgentForPassword.email)}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      if (!userResponse.data?.id) {
        toast.error('No user account found for this agent');
        return;
      }
      
      await axios.post(
        `${API}/api/auth/admin/set-password/${userResponse.data.id}`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      toast.success(`Password set for ${selectedAgentForPassword.name}`);
      setShowPasswordModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    }
  };

  const requestSIN = async (agent) => {
    if (!agent.email) {
      toast.error(`${agent.name} does not have an email address`);
      return;
    }
    
    try {
      toast.loading(`Sending SIN request to ${agent.email}...`, { id: 'sin-request' });
      
      // Use mortgage endpoint if has mortgage license, otherwise insurance
      const endpoint = agent.mortgage_id 
        ? `${API}/api/agents/${agent.mortgage_id}/request-sin`
        : `${API}/api/insurance/agents/${agent.insurance_id}/request-sin`;
      
      const response = await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      toast.success(response.data.message || `SIN request sent to ${agent.email}`, { id: 'sin-request' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send SIN request', { id: 'sin-request' });
    }
  };

  const filteredAgents = agents.filter(a => {
    const matchesSearch = a.name?.toLowerCase().includes(search.toLowerCase()) ||
      a.email?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const activeCount = agents.filter(a => a.status === 'active').length;
  const inactiveCount = agents.filter(a => a.status !== 'active').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="agents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Agents
          </h1>
          <p className="text-slate-500 mt-1">Unified view of all agents across divisions</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} data-testid="add-agent-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Agent
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-slate-300 text-xs uppercase">Total Agents</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Home className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-blue-100 text-xs uppercase">Mortgage</p>
                <p className="text-2xl font-bold">{stats.mortgage_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-purple-100 text-xs uppercase">Insurance</p>
                <p className="text-2xl font-bold">{stats.insurance_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Briefcase className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-emerald-100 text-xs uppercase">Dual License</p>
                <p className="text-2xl font-bold">{stats.dual_license_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search agents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-agents"
          />
        </div>
        <Select value={divisionFilter} onValueChange={setDivisionFilter}>
          <SelectTrigger className="w-48" data-testid="division-filter">
            <SelectValue placeholder="Filter by division" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Divisions</SelectItem>
            <SelectItem value="mortgage">Mortgage Only</SelectItem>
            <SelectItem value="insurance">Insurance Only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status ({agents.length})</SelectItem>
            <SelectItem value="active">Active ({activeCount})</SelectItem>
            <SelectItem value="inactive">Inactive ({inactiveCount})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Agent Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredAgents.map((agent) => (
          <Card 
            key={agent.id}
            className={`border-slate-100 shadow-sm hover:shadow-md transition-shadow ${agent.status === 'inactive' ? 'opacity-60' : ''}`}
            data-testid={`agent-card-${agent.id}`}
          >
            <CardContent className="p-5">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold ${
                    agent.has_mortgage_license && agent.has_insurance_license 
                      ? 'bg-gradient-to-br from-blue-500 to-purple-500' 
                      : agent.has_mortgage_license 
                        ? 'bg-blue-500' 
                        : 'bg-purple-500'
                  }`}>
                    {agent.name?.charAt(0) || 'A'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{agent.name}</h3>
                    <div className={`text-xs px-2 py-0.5 rounded-full inline-block ${
                      agent.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {agent.status}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(agent)} data-testid={`edit-agent-${agent.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  {agent.email && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => openPasswordModal(agent)} 
                      className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                      title="Manage Password"
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(agent)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-1.5 text-sm mb-3">
                {agent.email && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-3.5 h-3.5 text-slate-400" />
                    <span className="truncate">{agent.email}</span>
                  </div>
                )}
                {agent.phone && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-3.5 h-3.5 text-slate-400" />
                    <span>{agent.phone}</span>
                  </div>
                )}
              </div>

              {/* Licenses Section */}
              <div className="border-t border-slate-100 pt-3 mb-3">
                <p className="text-xs text-slate-500 uppercase mb-2 font-medium">License</p>
                <div className="flex gap-2 flex-wrap">
                  {agent.has_mortgage_license && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      <Home className="w-3 h-3" />
                      Mortgage
                    </span>
                  )}
                  {agent.has_insurance_license && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                      <Shield className="w-3 h-3" />
                      Insurance
                    </span>
                  )}
                  {agent.has_mortgage_license && agent.has_insurance_license && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                      Dual Licensed
                    </span>
                  )}
                </div>
              </div>

              {/* Commission & SIN */}
              <div className="flex items-center justify-between text-sm mb-3">
                <div>
                  <span className="text-slate-500">Total Earned:</span>
                  <span className="ml-1 font-bold text-emerald-600 font-mono">{formatCurrency(agent.total_commissions)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-400 text-xs">SIN:</span>
                  {agent.sin_masked ? (
                    <>
                      <span className="font-mono text-xs">{showSin[agent.id] ? agent.sin : agent.sin_masked}</span>
                      <button 
                        onClick={() => setShowSin(prev => ({...prev, [agent.id]: !prev[agent.id]}))}
                        className="text-blue-500 text-xs hover:underline ml-1"
                      >
                        {showSin[agent.id] ? 'Hide' : 'Show'}
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-xs text-amber-600">Not on file</span>
                      {agent.email && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-1.5 text-xs text-blue-600"
                          onClick={() => requestSIN(agent)}
                        >
                          <Mail className="w-3 h-3 mr-0.5" />
                          Request
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Commission Breakdown - Clickable */}
              {(agent.mortgage_commissions > 0 || agent.insurance_commissions > 0) && (
                <div className="bg-slate-50 rounded-lg p-2 text-xs space-y-1">
                  {agent.mortgage_commissions > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Switch to mortgage business unit and navigate to commissions with agent filter
                        localStorage.setItem('businessUnit', 'mortgage');
                        window.location.href = `/commissions?agent=${encodeURIComponent(agent.name)}&agentId=${agent.id}`;
                      }}
                      className="flex justify-between w-full hover:bg-blue-50 rounded px-1 py-0.5 transition-colors group cursor-pointer"
                      data-testid={`mortgage-commission-link-${agent.id}`}
                    >
                      <span className="text-blue-600 flex items-center gap-1">
                        <Home className="w-3 h-3" /> Mortgage
                      </span>
                      <span className="font-mono font-medium flex items-center gap-1">
                        {formatCurrency(agent.mortgage_commissions)}
                        <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </span>
                    </button>
                  )}
                  {agent.insurance_commissions > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Switch to insurance business unit and navigate to commissions with agent filter
                        localStorage.setItem('businessUnit', 'insurance');
                        window.location.href = `/commissions?agent=${encodeURIComponent(agent.name)}&agentId=${agent.id}`;
                      }}
                      className="flex justify-between w-full hover:bg-purple-50 rounded px-1 py-0.5 transition-colors group cursor-pointer"
                      data-testid={`insurance-commission-link-${agent.id}`}
                    >
                      <span className="text-purple-600 flex items-center gap-1">
                        <Shield className="w-3 h-3" /> Insurance
                      </span>
                      <span className="font-mono font-medium flex items-center gap-1">
                        {formatCurrency(agent.insurance_commissions)}
                        <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </span>
                    </button>
                  )}
                </div>
              )}

              {/* Portal Link */}
              <div className="border-t border-slate-100 pt-3 mt-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Link2 className="w-3 h-3" /> Portal Link
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => copyToClipboard(getAgentPortalLink(agent.id), agent.name)}
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => window.open(getAgentPortalLink(agent.id), '_blank')}
                    >
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredAgents.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No agents found</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl" data-testid="agent-modal">
          <DialogHeader>
            <DialogTitle>{editingAgent ? 'Edit Agent' : 'Add New Agent'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-6 py-4">
              {/* License Selection */}
              <div className="bg-slate-50 rounded-lg p-4">
                <Label className="text-sm font-semibold mb-3 block">Select Licenses *</Label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border-2 transition-all hover:border-blue-300 has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50">
                    <Checkbox 
                      checked={formData.has_mortgage_license}
                      onCheckedChange={(checked) => setFormData({...formData, has_mortgage_license: checked})}
                      data-testid="new-agent-mortgage-license"
                    />
                    <div className="flex items-center gap-2">
                      <Home className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium">Mortgage</p>
                        <p className="text-xs text-slate-500">Mortgage Division</p>
                      </div>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border-2 transition-all hover:border-purple-300 has-[:checked]:border-purple-500 has-[:checked]:bg-purple-50">
                    <Checkbox 
                      checked={formData.has_insurance_license}
                      onCheckedChange={(checked) => setFormData({...formData, has_insurance_license: checked})}
                      data-testid="new-agent-insurance-license"
                    />
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-purple-500" />
                      <div>
                        <p className="font-medium">Insurance</p>
                        <p className="text-xs text-slate-500">Insurance & Investment</p>
                      </div>
                    </div>
                  </label>
                </div>
                {!formData.has_mortgage_license && !formData.has_insurance_license && (
                  <p className="text-xs text-amber-600 mt-2">Please select at least one license</p>
                )}
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    data-testid="agent-name-input"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    data-testid="agent-email-input"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    data-testid="agent-phone-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    data-testid="agent-address-input"
                  />
                </div>
                <div>
                  <Label>Employment Type</Label>
                  <Select value={formData.employment_type} onValueChange={(v) => setFormData({...formData, employment_type: v})}>
                    <SelectTrigger data-testid="employment-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contractor">Contractor (T4A)</SelectItem>
                      <SelectItem value="employee">Employee (T4)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Social Insurance Number (SIN)</Label>
                  <Input
                    value={formData.sin}
                    onChange={(e) => setFormData({...formData, sin: e.target.value})}
                    placeholder="XXX-XXX-XXX"
                    data-testid="agent-sin-input"
                  />
                </div>
              </div>

              {/* Mortgage Settings */}
              {formData.has_mortgage_license && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-blue-600 flex items-center gap-2 mb-3">
                    <Home className="w-4 h-4" /> Mortgage Settings
                  </h4>
                  <div>
                    <Label>Commission Split %</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={formData.split_percentage}
                      onChange={(e) => setFormData({...formData, split_percentage: parseFloat(e.target.value)})}
                      data-testid="mortgage-split-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Enter as decimal (e.g., 0.85 for 85%)</p>
                  </div>
                </div>
              )}

              {/* Insurance Settings */}
              {formData.has_insurance_license && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-purple-600 flex items-center gap-2 mb-3">
                    <Shield className="w-4 h-4" /> Insurance Settings
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>FYO Split %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.fyo_split}
                        onChange={(e) => setFormData({...formData, fyo_split: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>FYC Split %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.fyc_split}
                        onChange={(e) => setFormData({...formData, fyc_split: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Renewal Split %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.renewal_split}
                        onChange={(e) => setFormData({...formData, renewal_split: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Transfer Split %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.transfer_split}
                        onChange={(e) => setFormData({...formData, transfer_split: parseFloat(e.target.value)})}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={!formData.has_mortgage_license && !formData.has_insurance_license}
                data-testid="save-agent-btn"
              >
                {editingAgent ? 'Update' : 'Create'} Agent
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Management Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-md" data-testid="password-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Manage Password
            </DialogTitle>
          </DialogHeader>
          
          {selectedAgentForPassword && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="font-medium">{selectedAgentForPassword.name}</p>
                <p className="text-sm text-slate-500">{selectedAgentForPassword.email}</p>
              </div>
              
              {passwordInfo && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Using default password:</span>
                    <span className={passwordInfo.is_default_password ? 'text-amber-600 font-medium' : 'text-emerald-600'}>
                      {passwordInfo.is_default_password ? 'Yes (agent123)' : 'No (custom)'}
                    </span>
                  </div>
                  {passwordInfo.password_set_at && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Last changed:</span>
                      <span>{new Date(passwordInfo.password_set_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="border-t pt-4">
                <Label>Set New Password</Label>
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  className="mt-1"
                  data-testid="new-password-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  The agent will be prompted to change this password on next login.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={setAgentPassword}
              disabled={!newPassword || newPassword.length < 6}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Set Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
